import {defineStore} from 'pinia'
import {useUserStore} from '~/stores/user'
import {getFavorites, addFavorite, removeFavorite} from '~/api/favorites'
import {useBehaviorFlow} from '~/composables/useBehaviorFlow'

// BI-UI v2:portal_favorites(implementation.md §5)。收藏端點不接受
// analytics_context(reservation-server favoriteRequest 無此欄位,domain success
// 以空字串記 session/flow——見 backend/reservation-server/internal/rest/favorites.go
// recordFavoriteDomainSuccess),故這裡只送 client 端互動事件,不嘗試把 request body
// 接上 analytics_context。
const FEATURE_ID = 'portal_favorites'

// 未登入時的暫存 key;登入後合併進 API 並清空(見 flushOnLogin)
const STORAGE_KEY = 'os_fav_space_ids'

interface FavoritesState {
    ids: number[]
    loaded: boolean
}

export const useFavoritesStore = defineStore('favorites', {
    state: (): FavoritesState => ({
        ids: [],
        loaded: false,
    }),
    getters: {
        isFavorited: (state) => (spaceId: number): boolean => state.ids.includes(spaceId),
        count: (state): number => state.ids.length,
    },
    actions: {
        // localStorage 僅 client 端可用;訪客收藏暫存於此
        readLocal(): number[] {
            if (!import.meta.client) return []
            try {
                const raw = localStorage.getItem(STORAGE_KEY)
                const parsed = raw ? JSON.parse(raw) : []
                return Array.isArray(parsed) ? parsed : []
            } catch {
                return []
            }
        },
        writeLocal(ids: number[]): void {
            if (!import.meta.client) return
            localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
        },
        // 初始化收藏集合:登入→API、訪客→localStorage。冪等(重覆呼叫只抓一次)。
        async init(): Promise<void> {
            if (this.loaded) return
            const userStore = useUserStore()
            if (userStore.getToken) {
                try {
                    const res = await getFavorites()
                    this.ids = res?.space_ids ?? []
                } catch {
                    this.ids = []
                }
            } else {
                this.ids = this.readLocal()
            }
            this.loaded = true
        },
        // 切換收藏:依登入狀態走 API 或 localStorage。樂觀更新,失敗回滾。
        async toggle(spaceId: number): Promise<void> {
            const userStore = useUserStore()
            const wasFavorited = this.ids.includes(spaceId)
            const op = wasFavorited ? 'remove' : 'add'
            // 不 mutate,建立新陣列
            this.ids = wasFavorited
                ? this.ids.filter(id => id !== spaceId)
                : [spaceId, ...this.ids]

            if (!userStore.getToken) {
                this.writeLocal(this.ids)
                return
            }
            const behaviorFlow = useBehaviorFlow()
            behaviorFlow.startTask(FEATURE_ID, op, op)
            try {
                await (wasFavorited ? removeFavorite(spaceId) : addFavorite(spaceId))
                behaviorFlow.trackStep(FEATURE_ID, op, 'client_confirmed')
                behaviorFlow.completeTask(FEATURE_ID)
            } catch {
                behaviorFlow.trackFailed(FEATURE_ID, op, 'network')
                // 回滾到切換前的狀態
                this.ids = wasFavorited
                    ? [spaceId, ...this.ids]
                    : this.ids.filter(id => id !== spaceId)
            }
        },
        // 登入後合併(web 端 merge):把訪客暫存的收藏逐一寫入 API,成功後清 localStorage,
        // 再以 API 為單一事實來源重載。
        async flushOnLogin(): Promise<void> {
            if (import.meta.client) {
                const local = this.readLocal()
                if (local.length > 0) {
                    await Promise.all(local.map(id => addFavorite(id).catch(() => {})))
                    localStorage.removeItem(STORAGE_KEY)
                }
            }
            this.loaded = false
            await this.init()
        },
        // 登出:回到訪客(localStorage)收藏狀態
        resetToLocal(): void {
            this.ids = this.readLocal()
            this.loaded = true
        },
    },
})
